const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAIL_LEN = 254;
const MAX_FIELD_LEN = 200;
const MAX_MESSAGE_LEN = 5000;

export const CONTACT_TYPES = ["partner", "press", "investor", "general"] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];

export const REQUIRED_FIELDS_BY_TYPE: Record<ContactType, readonly string[]> = {
  partner: ["email", "company", "role", "message"],
  press: ["email", "outlet", "message"],
  investor: ["email", "firm", "message"],
  general: ["email", "name", "message"],
};

export type ContactPayload = {
  type: ContactType;
  email: string;
  message: string;
  company?: string;
  role?: string;
  outlet?: string;
  deadline?: string;
  firm?: string;
  aum?: string;
  name?: string;
};

export type ValidationResult =
  | { ok: true; payload: ContactPayload }
  | { ok: false; error: string };

function isContactType(v: unknown): v is ContactType {
  return typeof v === "string" && (CONTACT_TYPES as readonly string[]).includes(v);
}

function readString(input: unknown, max: number): string | null {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (trimmed.length === 0 || trimmed.length > max) return null;
  return trimmed;
}

export function validateContact(input: unknown): ValidationResult {
  if (typeof input !== "object" || input === null) {
    return { ok: false, error: "Invalid request body." };
  }
  const raw = input as Record<string, unknown>;

  if (!isContactType(raw.type)) {
    return { ok: false, error: "Invalid contact type." };
  }
  const type = raw.type;

  const email = readString(raw.email, MAX_EMAIL_LEN);
  if (!email || !EMAIL_RE.test(email)) {
    return { ok: false, error: "Enter a valid email address." };
  }

  const message = readString(raw.message, MAX_MESSAGE_LEN);
  if (!message) {
    return { ok: false, error: "Message is required." };
  }

  const payload: ContactPayload = { type, email, message };

  for (const field of REQUIRED_FIELDS_BY_TYPE[type]) {
    if (field === "email" || field === "message") continue;
    const v = readString(raw[field], MAX_FIELD_LEN);
    if (!v) {
      return { ok: false, error: `${field} is required.` };
    }
    (payload as Record<string, unknown>)[field] = v;
  }

  for (const optional of ["deadline", "aum"] as const) {
    const v = readString(raw[optional], MAX_FIELD_LEN);
    if (v) (payload as Record<string, unknown>)[optional] = v;
  }

  return { ok: true, payload };
}
